import React, { useEffect, useState } from 'react';
import { useNewSurgerie } from '../../hooks/useNewSurgerie';
import { InputAdornment, Radio, styled, TextField, Typography } from '@mui/material';
import InsideLoading from 'components/loading/InsideLoading';
import { Hospital } from 'types/hospital';
import useSearch from 'hooks/search';
import NoData from 'components/no-data/NoData';
import { moneyFormat } from 'helpers/moneyFormat';
import { usePermissionRules } from 'hooks/permissionRules';
import {
  CardBody,
  GuidedCard,
  GuidedHeader,
  palette,
  PriceBox,
  PriceRange,
  SelectionCard,
  SelectionContent,
  ThreeGrid,
} from '../../components/GuidedSurgerie';
import ChargedValue from '../../components/ChargedValue';
import { BusinessOutlined, SearchOutlined } from '@mui/icons-material';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}));

const HospitalMeta = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 260px',
  gap: 12,
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const SurgerieFourthStep: React.FC = () => {
  const { surgerie, handleChange, hospitals, loadingHospitals } = useNewSurgerie();
  const { checkPermission } = usePermissionRules();
  const [value, setValue] = useState('');
  const [filtered, setFiltered] = useState<Hospital[]>([]);
  const search = useSearch();

  useEffect(() => {
    setFiltered(hospitals);
  }, [hospitals]);

  useEffect(() => {
    if (value) {
      const _rules = search(value, 'company_name', hospitals);
      setFiltered(_rules);
      return;
    }

    setFiltered(hospitals);
  }, [value, hospitals]);

  return (
    <>
      {loadingHospitals ? (
        <InsideLoading />
      ) : (
        <GuidedCard>
          <GuidedHeader
            step={4}
            title="Hospital"
            description="Escolha uma estrutura compatível com o procedimento e confira a faixa sugerida."
          />
          <CardBody>
            <TextField
              value={value}
              onChange={e => setValue(e.target.value)}
              label="Informe o hospital"
              placeholder="Digite o nome do hospital"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            {filtered.length === 0 ? (
              <NoData message="Nenhum hospital encontrado" />
            ) : (
              <Container>
                {filtered.map(hospital => {
                  const selected = surgerie.hospital_id === hospital.id;
                  const procedure = hospital.procedures[0];
                  return (
                    <SelectionCard
                      selected={selected}
                      key={hospital.id}
                      onClick={() => {
                        handleChange('hospital_id', hospital.id);
                        handleChange('hospital', hospital);
                      }}
                    >
                      <Radio checked={selected} value={hospital.id} />
                      <SelectionContent>
                        <HospitalMeta>
                          <div>
                            <Typography variant="body2" fontWeight={700} color={palette.ink}>
                              {hospital.company_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {hospital.address?.city || 'Cidade não informada'}
                              {hospital.address?.state ? ` - ${hospital.address.state}` : ''}
                            </Typography>
                          </div>

                          {checkPermission('surgeries.new.change-hospital-value') && selected && (
                            <ChargedValue
                              title="Editar valor do hospital"
                              value={surgerie.value_hospital}
                              min={procedure?.sale_price_min}
                              max={procedure?.sale_price_max}
                              onChange={v => handleChange('value_hospital', v)}
                            />
                          )}
                        </HospitalMeta>

                        <PriceRange>
                          <PriceBox>
                            <Typography variant="caption" color="textSecondary">
                              Preço mínimo
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="#b42318">
                              {moneyFormat(procedure?.sale_price_min || 0)}
                            </Typography>
                          </PriceBox>
                          <PriceBox>
                            <Typography variant="caption" color="textSecondary">
                              Preço máximo sugerido
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="success.main">
                              {moneyFormat(procedure?.sale_price_max || 0)}
                            </Typography>
                          </PriceBox>
                        </PriceRange>

                        {selected && (
                          <ThreeGrid>
                            <PriceBox>
                              <BusinessOutlined color="primary" fontSize="small" />
                              <Typography variant="caption" display="block">
                                Estrutura cadastrada
                              </Typography>
                            </PriceBox>
                            <PriceBox>
                              <Typography variant="caption" color="textSecondary">
                                Setor
                              </Typography>
                              <Typography variant="body2">{hospital.sector || 'Não informado'}</Typography>
                            </PriceBox>
                            <PriceBox>
                              <Typography variant="caption" color="textSecondary">
                                Telefone
                              </Typography>
                              <Typography variant="body2">
                                {hospital.phone || hospital.fixed_phone || 'Não informado'}
                              </Typography>
                            </PriceBox>
                          </ThreeGrid>
                        )}
                      </SelectionContent>
                    </SelectionCard>
                  );
                })}
              </Container>
            )}
          </CardBody>
        </GuidedCard>
      )}
    </>
  );
};

export default SurgerieFourthStep;
